import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHighestContrast, contrastRatio, relativeLuminance, parseColor } from '../utils/luminance';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation.
    `apply-now` and `resident-access` are intentionally NOT excluded — they
    are nested under `contact` via `parent` in pages.json and surface as
    dropdown/child links under "Contact Us". */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Office-hours helpers (used by the mobile/tablet drawer's HOURS section).
// Kept local to this file rather than shared with Footer's copy — the two
// components have divergent styling wrappers and lifting these ~30 lines
// into a shared util isn't a large enough win to justify a new file.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function hasHoursValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const n = val.trim().toLowerCase();
  return n !== '' && n !== 'false' && n !== 'null' && n !== 'undefined' && n !== '0' && n !== 'closed';
}

function formatHoursTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const entriesByDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasHoursValue(entry.day)) continue;
    const key = entry.day!.trim().toLowerCase().slice(0, 3);
    if (DAY_ORDER.includes(key) && !entriesByDay.has(key)) {
      entriesByDay.set(key, entry);
    }
  }
  const groups: GroupedHours[] = [];
  for (const key of DAY_ORDER) {
    const entry = entriesByDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed = !entry || !hasHoursValue(entry.open_time) || !hasHoursValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatHoursTime(entry.open_time || '')} – ${formatHoursTime(entry.close_time || '')}`;
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' – ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} – ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Book A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^+\d]/g, '');
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `tel:${digits}` : raw;
}

/**
 * Strip a trailing country segment ("USA", "U.S.A.", "United States",
 * "United States of America") from the property address. Runs against the
 * final comma-separated segment so we never chew into a city/state that
 * happens to contain "US" as a substring.
 */
function stripCountry(address: string | null | undefined): string {
  if (!address) return '';
  const trimmed = address.trim();
  const lastComma = trimmed.lastIndexOf(',');
  if (lastComma === -1) return trimmed;
  const tail = trimmed.slice(lastComma + 1).trim().toLowerCase().replace(/\./g, '');
  if (
    tail === 'usa' ||
    tail === 'us' ||
    tail === 'united states' ||
    tail === 'united states of america'
  ) {
    return trimmed.slice(0, lastComma).trim();
  }
  return trimmed;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link (with optional hover/focus dropdown)
// ============================================================================

function DesktopNavLink({ link, isActive }: { link: NavLink; isActive: (href: string) => boolean }) {
  const hasChildren = !!link.children?.length;
  return (
    <div className={`sh-navitem${hasChildren ? ' sh-navitem--has-children' : ''}`}>
      <a
        href={link.href}
        className={`sh-navlink${isActive(link.href) ? ' sh-navlink--active' : ''}`}
      >
        {link.label}
        {hasChildren ? <ChevronDownIcon className="sh-navlink-caret" /> : null}
      </a>
      {hasChildren ? (
        <div className="sh-dropdown" role="menu">
          {link.children!.map((child) => (
            <a
              key={child.href}
              href={child.href}
              role="menuitem"
              className={`sh-dropdown-link${isActive(child.href) ? ' sh-dropdown-link--active' : ''}`}
            >
              {child.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================
// Renders a single row of the off-canvas drawer's nav. Parents with children
// render as [<link> <chevron toggle>]; the chevron toggles a collapsible
// children container in the caller. Children render as indented plain links
// (no chevron). This mirrors the desktop dropdown's "hover to reveal" pattern
// with a touch-friendly click-to-expand instead of pure :hover, which touch
// screens don't fire reliably.
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
  isChild,
  isOpen,
  onToggleChildren,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
  isChild?: boolean;
  isOpen?: boolean;
  onToggleChildren?: () => void;
}) {
  const hasChildren = !!link.children?.length;
  const rowClasses = [
    'menu-nav-row',
    hasChildren ? 'menu-nav-row--has-children' : '',
    isChild ? 'menu-nav-row--child' : '',
  ].filter(Boolean).join(' ');
  const linkClasses = [
    'menu-nav-link',
    'block',
    'py-2.5',
    'md:py-3',
    'font-body',
    'text-base',
    'md:text-lg',
    'font-medium',
    'tracking-wide',
    'text-white',
    'transition-colors',
    isActive ? 'menu-nav-link--active' : '',
    isChild ? 'menu-nav-link--child' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={rowClasses}>
      <a href={link.href} onClick={onNavigate} className={linkClasses}>
        <span className="inline-flex items-center gap-2">{link.label}</span>
      </a>
      {hasChildren ? (
        <button
          type="button"
          onClick={onToggleChildren}
          className={`menu-nav-toggle${isOpen ? ' menu-nav-toggle--open' : ''}`}
          aria-expanded={!!isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${link.label} submenu`}
        >
          <ChevronDownIcon className="menu-nav-toggle-icon" />
        </button>
      ) : null}
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: A floating, primary-colored "pill" navigation bar.
//
// - Mobile (<md): logo on the left, hamburger on the right. Nav lives in the
//   full-height drawer.
// - Tablet (md — <lg): hamburger on the LEFT, logo CENTER, "Book A Tour" CTA
//   on the RIGHT. Nav links stay hidden and live in the drawer. This is the
//   "compact" three-column layout matching the tablet reference.
// - Desktop (lg+): three-column layout — nav links on the left, logo center,
//   the remaining nav links plus "Book A Tour" on the right. No hamburger.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Tracks which parent nav items are expanded in the drawer. Keyed by href so
  // the state survives navLinks recomputation without stale entries pinning
  // groups open. Reset whenever the drawer closes so it opens fresh next time.
  const [openParents, setOpenParents] = useState<Set<string>>(new Set());

  // Scroll detection — adds a subtle elevation/shrink once the page scrolls.
  useEffect(() => {
    let ticking = false;
    const SCROLL_AT = 20;
    const UNSCROLL_AT = 8;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (!prev && y > SCROLL_AT) return true;
          if (prev && y < UNSCROLL_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const pageCtx = usePage();

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive leasing
  // view" widget on that page. We require BOTH the JavaScript ID and the
  // Config ID; a property with only one populated is a half-configured state.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Home + the dynamic pages, split evenly around the center logo.
  const allLinks = useMemo<NavLink[]>(
    () => [{ label: 'Home', href: '/' }, ...navLinks],
    [navLinks],
  );
  const [leftLinks, rightLinks] = useMemo(() => {
    const mid = Math.ceil(allLinks.length / 2);
    return [allLinks.slice(0, mid), allLinks.slice(mid)] as const;
  }, [allLinks]);

  const currentPath = pageCtx?.path
    || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isNavActive = useCallback((href: string) => {
    const normalized = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const target = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;
    if (target === '/') return normalized === '/';
    return normalized === target || normalized.startsWith(`${target}/`);
  }, [currentPath]);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyName = usePropertyField('property_name') as string | null | undefined;
  const defaultTelUrl = toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);

  // Office hours — drives the "HOURS" block at the bottom of the drawer.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = useMemo(
    () => (officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : []),
    [officeHours],
  );

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const headerLogo = monoWhiteLogo || logoRaw.image;

  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);

  // Auto-pick a readable label color for the "Book A Tour" CTA against its
  // actual accent-colored surface, so light accents get dark text and dark
  // accents keep white text. We prefer the theme's own on-color tokens
  // (`textOnPrimary` / `textOnSecondary`) since they usually match the brand;
  // if neither clears WCAG AA 4.5:1 against the accent, we synthesize a
  // near-black / pure-white from the accent's relative luminance so the
  // label stays legible even with mis-configured or missing on-color tokens.
  // Falls back to raw `--vibe-color-accent` unresolved → keeps the previous
  // hardcoded white text behavior server-side / before tokens hydrate.
  const tokens = useTokens();
  const bookBtnTextColor = useMemo(() => {
    const accent = tokens?.color?.accent;
    if (!accent || !parseColor(accent)) return null;
    const onPrimary = tokens?.color?.textOnPrimary;
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onPrimary, onSecondary].filter(
      (c): c is string => typeof c === 'string' && c.length > 0,
    );
    const picked = candidates.length ? pickHighestContrast(accent, candidates) : null;
    if (picked && contrastRatio(accent, picked) >= 4.5) return picked;
    return relativeLuminance(accent) > 0.55 ? '#1a1a1a' : '#ffffff';
  }, [tokens]);
  const bookBtnStyle = bookBtnTextColor
    ? ({ ['--sh-book-btn-text' as string]: bookBtnTextColor } as React.CSSProperties)
    : undefined;

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || defaultTelUrl;
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const menuLogoImage = monoWhiteLogo || logoRaw.image;
  const menuLogo = { ...logoRaw, image: menuLogoImage };

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // When the drawer opens, pre-expand the parent group that contains the
  // currently active page so the active child is visible without an extra tap.
  // When it closes, collapse everything so the next open reads as a fresh state.
  useEffect(() => {
    if (!menuOpen) {
      setOpenParents(new Set());
      return;
    }
    const activeParents = new Set<string>();
    for (const link of navLinks) {
      if (link.children?.some((child) => isNavActive(child.href))) {
        activeParents.add(link.href);
      }
    }
    if (activeParents.size > 0) setOpenParents(activeParents);
  }, [menuOpen, navLinks, isNavActive]);

  const closeMobileMenu = () => setMenuOpen(false);

  const toggleParentOpen = useCallback((href: string) => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });
  }, []);

  const LogoLink = (
    <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
      <a href="/" className="sh-logo" aria-label="Home">
        {headerLogo ? (
          <VibesitesImage
            url={headerLogo}
            alt={logoRaw.alt || 'Community Logo'}
            className="sh-logo-img"
          />
        ) : (
          <span className="sh-logo-fallback">{pageCtx?.alias ? 'Home' : 'Stadium House'}</span>
        )}
      </a>
    </EditableSlotGroup>
  );

  return (
    <>
      <header
        className={`sh-header fixed left-0 right-0 w-full ${menuOpen ? 'z-[70]' : 'z-50'}${scrolled ? ' sh-header--scrolled' : ''}`}
        style={{ top: 0 }}
      >
        <div
          className="sh-header-inner"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}
        >
          <div className="sh-navbar">
            {/* Left nav (desktop) */}
            <nav className="sh-nav-side sh-nav-left" aria-label="Primary">
              {leftLinks.map((link) => (
                <DesktopNavLink key={link.href} link={link} isActive={isNavActive} />
              ))}
            </nav>

            {/* Center logo */}
            {LogoLink}

            {/* Right nav (desktop) + CTA */}
            <nav className="sh-nav-side sh-nav-right" aria-label="Primary">
              {rightLinks.map((link) => (
                <DesktopNavLink key={link.href} link={link} isActive={isNavActive} />
              ))}
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  className="sh-book-btn"
                  style={bookBtnStyle}
                >
                  {scheduleTourLink.label || 'Book A Tour'}
                </a>
              </EditableSlotGroup>
            </nav>

            {/* Hamburger (mobile + tablet). Positioned right on mobile via
                the flex layout, and pinned to grid column 1 (left) on tablet
                via the sh-hamburger CSS. Hidden on desktop. */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="sh-hamburger"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span className={`sh-hamburger-bar ${menuOpen ? 'rotate-45 translate-y-[0px]' : '-translate-y-[6px]'}`} />
              <span className={`sh-hamburger-bar ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`sh-hamburger-bar ${menuOpen ? '-rotate-45 translate-y-[0px]' : 'translate-y-[6px]'}`} />
            </button>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Off-canvas drawer — slides in from the RIGHT on tablet-and-   */}
        {/* smaller. Solid primary background with logo, nav, and         */}
        {/* Visit / Contact / Hours blocks stacked in a single column.    */}
        {/* Desktop (lg+) never sees this — the hamburger is hidden.      */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`menu-backdrop fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 lg:hidden ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
        />

        <aside
          aria-hidden={!menuOpen}
          aria-label="Site menu"
          className={`mobile-menu-panel fixed right-0 top-0 bottom-0 z-[61] flex flex-col overflow-hidden transition-transform duration-300 ease-out lg:hidden ${
            menuOpen ? 'translate-x-0 mobile-menu-open' : 'translate-x-full pointer-events-none'
          }`}
        >
          {/* Close button pinned to the TOP-RIGHT of the panel, matching
              the drawer's right-edge anchor. Kept outside the scroll
              container so it stays anchored regardless of the drawer's
              inner scroll position. */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="menu-close-btn"
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <div className="menu-scroll flex-1 overflow-y-auto">
            {/* Brand block — big centered logo + property name */}
            <div className="menu-brand">
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" onClick={closeMobileMenu} className="menu-brand-logo-link" aria-label="Home">
                  {menuLogo.image ? (
                    <VibesitesImage
                      url={menuLogo.image}
                      alt={menuLogo.alt || 'Community Logo'}
                      sizes="180px"
                      className="menu-brand-logo"
                    />
                  ) : (
                    <span className="menu-brand-fallback">Stadium House</span>
                  )}
                </a>
              </EditableSlotGroup>
              {propertyName ? (
                <div className="menu-brand-name">{propertyName}</div>
              ) : null}
            </div>

            {/* Primary navigation. Parent items with children collapse their
                children into a submenu that opens on tap of the chevron (and
                on hover via CSS on pointer devices), mirroring the desktop
                nav's hover-to-reveal dropdown. */}
            <nav role="navigation" aria-label="Main navigation" className="menu-nav">
              <MobileNavLink
                link={{ label: 'Home', href: '/' }}
                onNavigate={closeMobileMenu}
                isActive={isNavActive('/')}
              />
              {navLinks.map((link) => {
                const isOpen = openParents.has(link.href);
                const hasChildren = !!link.children?.length;
                return (
                  <div
                    key={link.href}
                    className={`menu-nav-group${hasChildren ? ' menu-nav-group--has-children' : ''}${
                      isOpen ? ' menu-nav-group--open' : ''
                    }`}
                  >
                    <MobileNavLink
                      link={link}
                      onNavigate={closeMobileMenu}
                      isActive={isNavActive(link.href)}
                      isOpen={isOpen}
                      onToggleChildren={hasChildren ? () => toggleParentOpen(link.href) : undefined}
                    />
                    {hasChildren ? (
                      <div
                        className={`menu-nav-children${isOpen ? ' menu-nav-children--open' : ''}`}
                        aria-hidden={!isOpen}
                      >
                        {link.children!.map((child) => (
                          <MobileNavLink
                            key={child.href}
                            link={child}
                            onNavigate={closeMobileMenu}
                            isActive={isNavActive(child.href)}
                            isChild
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </nav>

            {/* Contact / hours info blocks — one per row, small caps
                heading over the value. Matches the reference. */}
            <div className="menu-info">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <div className="menu-info-block">
                  <div className="menu-info-label">Visit Us</div>
                  <a
                    href={addressHref}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="menu-info-value"
                  >
                    {stripCountry(addressLink.text) || '123 Main Street, Atlanta, GA 30313'}
                  </a>
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                <div className="menu-info-block">
                  <div className="menu-info-label">Contact Us</div>
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="menu-info-value dniphone dniphonehref"
                  >
                    {phoneDisplay}
                  </a>
                </div>
              </EditableSlotGroup>

              {groupedHours.length > 0 ? (
                <div className="menu-info-block">
                  <div className="menu-info-label">Hours</div>
                  <dl className="menu-hours">
                    {groupedHours.map((group, index) => (
                      <div key={index} className="menu-hours-row">
                        <dt>{group.label}</dt>
                        <dd>{group.hours}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}
            </div>
          </div>
        </aside>
      </header>

      <style>{`
        /* ================================================================ */
        /* Floating pill navbar                                            */
        /* ================================================================ */
        .sh-header-inner {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        @media (min-width: 768px) {
          .sh-header-inner {
            padding-left: 1.75rem;
            padding-right: 1.75rem;
          }
        }
        .sh-navbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 0.85rem;
          padding: 0.5rem 0.6rem 0.5rem 1.25rem;
          border-radius: 9999px;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
          transition: padding 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        }
        @media (min-width: 768px) {
          .sh-navbar {
            display: grid;
            /* minmax(0, 1fr) lets the side columns shrink to fit the container.
               Plain 1fr (which is minmax(auto, 1fr)) refuses to shrink below
               the nav's min-content width, which pushes the pill past its
               parent's padding when there are enough nav items. */
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
            align-items: center;
            padding: 0.4rem 1rem 0.4rem 0.75rem;
          }
        }
        @media (min-width: 1024px) {
          .sh-navbar {
            /* Symmetric padding — left nav hugs the pill's left edge and
               right nav + CTA hug the pill's right edge, so both sides need
               the same gutter to read as centered around the logo. */
            padding: 0.4rem 1.25rem;
          }
        }
        .sh-header--scrolled .sh-navbar {
          box-shadow: 0 10px 34px rgba(0, 0, 0, 0.28);
        }

        /* Center logo */
        .sh-logo {
          grid-column: 2;
          justify-self: center;
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        .sh-logo-img {
          width: auto;
          height: 3.25rem;
          max-height: 3.25rem;
          object-fit: contain;
        }
        @media (min-width: 1024px) {
          .sh-logo-img { height: 3.5rem; max-height: 3.5rem; }
        }
        .sh-logo-fallback {
          font-family: var(--vibe-font-heading);
          font-weight: 700;
          font-size: 1.15rem;
          color: var(--vibe-color-text-on-primary, #fff);
          white-space: nowrap;
        }

        /* Nav sides — hidden on mobile.
           On tablet (md — <lg) the LEFT nav is fully hidden and the RIGHT nav
           still renders so its child "Book A Tour" CTA can appear; the nav
           links inside it are hidden and only the CTA shows through.
           On desktop (lg+) both sides render their nav links normally. */
        .sh-nav-side {
          display: none;
        }
        @media (min-width: 768px) {
          .sh-nav-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.35rem;
            /* Pair with grid-template-columns: minmax(0, 1fr) — without
               min-width: 0 the flex container falls back to its content's
               intrinsic width, defeating the shrinkable grid column. */
            min-width: 0;
            grid-column: 3;
          }
          /* Tablet-only: hide the individual nav items so only the
             .sh-book-btn CTA is visible inside the right nav. */
          .sh-nav-right .sh-navitem { display: none; }
        }
        @media (min-width: 1024px) {
          .sh-nav-side {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            min-width: 0;
          }
          /* Justify links OUTWARD so each nav group hugs the pill's outer
             edge: left nav sits against the pill's left gutter, right nav
             (including the Book A Tour CTA as its last child) sits against
             the pill's right gutter. Combined with the auto-sized center
             column, this leaves the empty space between the innermost link
             and the logo — the visual balance point of the pill. */
          .sh-nav-left { grid-column: 1; justify-content: flex-start; }
          .sh-nav-right { grid-column: 3; justify-content: flex-end; }
          /* Un-hide the nav items on desktop — the tablet override above
             collapsed them so only the CTA remained visible. */
          .sh-nav-right .sh-navitem { display: inline-flex; }
        }

        .sh-navitem {
          position: relative;
          display: inline-flex;
        }
        .sh-navlink {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.55rem 0.6rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          line-height: 1;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 88%, transparent);
          text-decoration: none;
          white-space: nowrap;
          border-radius: 9999px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        @media (min-width: 1024px) {
          .sh-navlink { font-size: 0.95rem; padding: 0.55rem 0.7rem; }
        }
        .sh-navlink:hover {
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .sh-navlink--active {
          color: var(--vibe-color-text-on-primary, #fff);
          font-weight: 600;
        }
        .sh-navlink--active::after {
          content: '';
          position: absolute;
          left: 0.6rem;
          right: 0.6rem;
          bottom: 0.3rem;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
        }
        .sh-navlink-caret {
          width: 0.85rem;
          height: 0.85rem;
          opacity: 0.85;
          transition: transform 0.2s ease;
        }
        .sh-navitem:hover .sh-navlink-caret,
        .sh-navitem:focus-within .sh-navlink-caret {
          transform: rotate(180deg);
        }

        /* Dropdown */
        .sh-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 0.5rem;
          min-width: 12rem;
          padding: 0;
          overflow: hidden;
          background: var(--vibe-color-primary);
          border: none;
          border-radius: 0.75rem;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.2s;
          z-index: 10;
        }
        .sh-nav-right .sh-dropdown {
          left: auto;
          right: 0;
        }
        .sh-navitem:hover .sh-dropdown,
        .sh-navitem:focus-within .sh-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0s;
        }
        .sh-dropdown-link {
          display: block;
          padding: 0.75rem 1.1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--vibe-color-text-on-primary, #fff);
          text-decoration: none;
          border-radius: 0;
          white-space: nowrap;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .sh-dropdown-link + .sh-dropdown-link {
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 15%, transparent);
        }
        .sh-dropdown-link:hover {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
        }
        .sh-dropdown-link--active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          font-weight: 600;
        }

        /* Book A Tour CTA */
        .sh-book-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          /* No margin-left: auto — the parent .sh-nav-right is
             justify-content: flex-end at both tablet (768px+) and desktop
             (1024px+), so the CTA (as the last flex child) already sits at
             the pill's right edge. Adding an auto margin here would consume
             all the free space, pushing the sibling nav links to the LEFT
             of the right column and orphaning the CTA. */
          padding: 0.65rem 1.35rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          line-height: 1;
          white-space: nowrap;
          /* --sh-book-btn-text is set inline by the component and holds the
             WCAG-picked text color for the current accent. Fallback to #fff
             preserves the previous light-on-dark default when the theme has
             no accent token resolved (e.g. server render before hydration). */
          color: var(--sh-book-btn-text, #fff);
          background: var(--vibe-color-accent, #fff);
          border: 1px solid var(--vibe-color-accent, #fff);
          border-radius: 9999px;
          text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        .sh-book-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
          background: color-mix(in srgb, var(--vibe-color-accent, #fff), white 30%);
        }
        .sh-book-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Hamburger — visible on mobile (right side, via flex layout) and
           tablet (LEFT side of the grid). Hidden on desktop (lg+). */
        .sh-hamburger {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          flex-shrink: 0;
          border-radius: 9999px;
          background: transparent;
          border: none;
          cursor: pointer;
        }
        @media (min-width: 768px) {
          /* Grid layout kicks in — pin the hamburger to column 1 so it sits
             on the far left of the pill, matching the tablet reference.
             grid-row: 1 is required because the hamburger comes AFTER the
             logo and right-nav in DOM order; with default sparse packing
             the auto-placement cursor is already past column 1 and would
             wrap the hamburger onto a new row instead of backfilling. */
          .sh-hamburger {
            grid-column: 1;
            grid-row: 1;
            justify-self: start;
          }
        }
        @media (min-width: 1024px) {
          .sh-hamburger { display: none; }
        }
        .sh-hamburger-bar {
          position: absolute;
          display: block;
          width: 1.4rem;
          height: 2px;
          border-radius: 2px;
          background: var(--vibe-color-text-on-primary, #fff);
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        /* ================================================================= */
        /* Off-canvas drawer — tablet-and-smaller, slides in from the RIGHT */
        /* ================================================================= */
        .mobile-menu-panel {
          /* Reveal ~3rem of the underlying page on mobile so the user has
             an obvious "tap here to close" gutter. Fixed width on tablet
             so the panel doesn't grow awkwardly wide. */
          width: calc(100% - 3rem);
          max-width: 22rem;
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          padding: 1.25rem 1.5rem 2rem;
        }
        @media (min-width: 640px) {
          .mobile-menu-panel {
            max-width: 24rem;
            padding: 1.5rem 2rem 2.5rem;
          }
        }
        @media (min-width: 768px) {
          .mobile-menu-panel {
            max-width: 26rem;
          }
        }
        .mobile-menu-panel.mobile-menu-open {
          box-shadow: -16px 0 50px rgba(0, 0, 0, 0.4);
        }
        .menu-backdrop {
          cursor: pointer;
        }
        .menu-scroll {
          display: flex;
          flex-direction: column;
        }

        /* Close button — top RIGHT of the panel */
        .mobile-menu-panel .menu-close-btn {
          align-self: flex-end;
          width: 2.5rem;
          height: 2.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.9);
          background: transparent;
          border: none;
          cursor: pointer;
          margin: 0 -0.5rem 0.5rem 0;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .menu-close-btn:hover {
          color: #fff;
        }
        .mobile-menu-panel .menu-close-btn svg {
          width: 1.5rem;
          height: 1.5rem;
        }

        /* Brand block */
        .menu-brand {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0.5rem 0 1.75rem;
        }
        .menu-brand-logo-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .menu-brand-logo {
          height: 6rem;
          max-height: 6rem;
          width: auto;
          object-fit: contain;
        }
        @media (min-width: 640px) {
          .menu-brand-logo {
            height: 7rem;
            max-height: 7rem;
          }
        }
        .menu-brand-fallback {
          font-family: var(--vibe-font-heading);
          font-weight: 700;
          font-size: 1.75rem;
          color: #fff;
        }
        .menu-brand-name {
          margin-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.5rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: #fff;
        }

        /* Nav links */
        .menu-nav {
          display: flex;
          flex-direction: column;
          padding-bottom: 1.5rem;
        }
        /* A "group" wraps a parent nav row + its collapsible children. Parents
           without children still render as a group (single row) so the layout
           stays uniform. */
        .menu-nav-group {
          display: flex;
          flex-direction: column;
        }
        /* Row = the parent link on the left + optional chevron toggle on the
           right. Uses flex so the toggle sits at the row's right edge without
           consuming click area from the link itself. */
        .menu-nav-row {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        .menu-nav-row--has-children .menu-nav-link {
          flex: 1 1 auto;
          min-width: 0;
        }
        /* Chevron toggle. Rotates 180° when the group is open, matching the
           desktop dropdown's caret-flip. */
        .menu-nav-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          padding: 0;
          margin: 0;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.85);
          cursor: pointer;
          border-radius: 9999px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .menu-nav-toggle:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.08);
        }
        .menu-nav-toggle-icon {
          width: 1rem;
          height: 1rem;
          transition: transform 0.25s ease;
        }
        .menu-nav-toggle--open .menu-nav-toggle-icon {
          transform: rotate(180deg);
        }
        /* Collapsible child container. Uses max-height animation so the
           children slide open/closed. 32rem is generous — no parent group
           has more than a few children, and the value only caps the animation
           end-state, not the actual rendered height. Indented via padding
           only — no tree-branch border, to match the clean reference. */
        .menu-nav-children {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          max-height: 0;
          padding-left: 1rem;
          transition: max-height 0.25s ease, padding-top 0.25s ease, padding-bottom 0.25s ease;
        }
        .menu-nav-children--open {
          max-height: 32rem;
          padding-top: 0.15rem;
          padding-bottom: 0.35rem;
        }
        /* Child link tweaks — slightly smaller, subtler weight than parents. */
        .menu-nav-link--child {
          font-size: 1rem;
          font-weight: 400;
        }
        /* Pointer-device hover: on tablets with a mouse/trackpad, hovering the
           parent group opens the submenu and closes it when the pointer leaves.
           Driven purely via CSS (not React state) so leaving the group
           implicitly collapses it — otherwise hover-open would stick after
           the pointer moved away because there's no hover-off event tied to
           state. Touch devices don't fire :hover; they use the chevron tap
           driven by JS state, which sets .menu-nav-children--open above. */
        @media (hover: hover) and (pointer: fine) {
          .menu-nav-group--has-children:hover .menu-nav-toggle-icon,
          .menu-nav-group--has-children:focus-within .menu-nav-toggle-icon {
            transform: rotate(180deg);
          }
          .menu-nav-group--has-children:hover .menu-nav-children,
          .menu-nav-group--has-children:focus-within .menu-nav-children {
            max-height: 32rem;
            padding-top: 0.15rem;
            padding-bottom: 0.35rem;
          }
        }
        .menu-nav-link {
          display: block;
          padding: 0.55rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-nav-link:hover {
          color: #fff;
        }
        .menu-nav-link--active {
          color: #fff;
          font-weight: 600;
        }
        /* Hover-underline: swipes in from the LEFT edge of the label so it
           reads as a "side" reveal, not a fade-in. Anchored to the inner
           inline-flex span so the line hugs the text width instead of
           stretching across the full-width block anchor. All drawer nav
           anchors wrap their label in '.menu-nav-link > span' so this
           selector matches every item consistently. */
        .menu-nav-link > span {
          position: relative;
        }
        .menu-nav-link > span::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -0.3rem;
          width: 0;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
          transition: width 0.3s ease;
          pointer-events: none;
        }
        .menu-nav-link:hover > span::after,
        .menu-nav-link:focus-visible > span::after {
          width: 100%;
        }
        /* Currently-selected page keeps the underline persistently drawn so
           the active state stays visible without needing hover. */
        .menu-nav-link--active > span::after {
          width: 100%;
        }

        /* Info blocks (Visit / Contact / Hours) */
        .menu-info {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.18);
        }
        .menu-info-block {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .menu-info-label {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #fff;
        }
        .menu-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-info-value:hover {
          color: #fff;
        }
        .menu-hours {
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .menu-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.95);
        }
        .menu-hours-row dt {
          margin: 0;
          font-weight: 500;
        }
        .menu-hours-row dd {
          margin: 0;
          font-weight: 400;
        }

        /* Global overrides for anchors within the panel */
        .mobile-menu-panel a {
          color: rgba(255, 255, 255, 0.95);
        }
        .mobile-menu-panel a:hover {
          color: #fff;
        }
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Book A Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        text: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.phone_link': {
      label: 'Phone Link',
      fields: {
        text: {
          label: 'Phone Text',
          type: 'text',
          defaultField: 'property_call_tracking_phone',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
